import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, ToggleButton, ToggleButtonGroup } from '@mui/material';

import Selector from '../../components/Selector';
import Tx from '../../components/Tx';
import PageTitle from '../../components/PageTitle';
import Icn from '../../components/Icn';
import Product from '../../components/Product';
import styles from './AIReport.module.css';
import { api } from '../../maps/data.js';
import { colors } from '../../maps/colors';
import AIReportProductsDialog from '../../components/dialogs/AIReportProductsDialog';
import SelectedProductList from '../../components/SelectedProductList';
import useInitialRender from '../../components/hooks/useInitialRender';

const daysOptions = Array(31).fill().map((_, index) => ({ label: index, value: index }));
const hoursOptions = Array(24).fill().map((_, index) => ({ label: index, value: index }));
const minutesOptions = [{ label: 0, value: 0 }, { label: 15, value: 15 }, { label: 30, value: 30 }, { label: 45, value: 45 }];

const INITIAL_PAGE = 1;
const INITIAL_DAYS = 1;
const SUCCESS_PERCENTAGE_RATE = 90;

const buttons = ['All Products', 'Selected Products'];

function AIReportPage() {
    const initialRender = useInitialRender();
    const page = useRef(INITIAL_PAGE);
    const [days, setDays] = useState(INITIAL_DAYS);
    const [hours, setHours] = useState(0);
    const [minutes, setMinutes] = useState(0);
    const [reports, setReports] = useState([]);
    const [selectedButton, setSelectedButton] = useState(buttons[0]);
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState([]);

    const { totalSuccess, totalCount } = useMemo(() => {
        return reports.reduce((acc, current) => {
            const { countGroundTruth1, countGroundTruth2 } = current;
            const total = countGroundTruth1 + countGroundTruth2;
            return { totalCount: acc.totalCount + total, totalSuccess: acc.totalSuccess + countGroundTruth1 };
        }, { totalSuccess: 0, totalCount: 0 });
    }, [reports]);

    const avarageRate = (totalSuccess / totalCount * 100).toFixed(2);

    const handleChange = (callback) => (e) => {
        callback(e.target.value);
    }

    const getPastAlerts = async (days, hours, minutes, products) => {
        const byDateParams = `&days=${days}&hours=${hours}&minutes=${minutes}`;
        const byProductsParams = `&products=${products}`;
        const params = products?.length > 0 ? byProductsParams : byDateParams;

        const res = await api({
            type: 'getPastAlerts',
            params: '?page=' + page.current + params
        });

        res.sort((a, b) => a.product.barcode - b.product.barcode);
        setReports(res);
    }

    const handleCloseDialog = () => {
        setSelectedButton(buttons[0]);
        setIsDialogVisible(false);
    }

    const handleSaveDialog = async (products) => {
        setSelectedProducts(products);
        setIsDialogVisible(false);
    }

    const handleDeleteSelectedProduct = (product) => {
        setSelectedProducts(prevProducts => prevProducts.filter(p => p._id !== product._id));
    }

    useEffect(() => {
        if (selectedButton === buttons[1]) setIsDialogVisible(true);
    }, [selectedButton]);

    useEffect(() => {
        if (!initialRender) {
            const barcodes = selectedProducts.map(p => p.barcode).join();
            getPastAlerts(days, hours, minutes, barcodes);
            selectedProducts.length === 0 && setSelectedButton(buttons[0]);
        }
    }, [selectedProducts]);

    useEffect(() => {
        page.current = INITIAL_PAGE;
        getPastAlerts(days, hours, minutes);
    }, [days, hours, minutes]);

    return (
        <div className={styles.container}>
            {/* Title */}
            <PageTitle>
                <Icn>graphIcon</Icn> <Tx>page.ai-report.title</Tx>
            </PageTitle>

            <div style={{ display: 'flex' }}>
                {/* Buttons Group */}
                <ToggleButtonGroup
                    color="primary"
                    value={selectedButton}
                    exclusive
                    className={styles.buttonsGroup}
                    onChange={(e, value) => {
                        value && setSelectedButton(value);
                        value === buttons[0] && setSelectedProducts([]);
                    }}
                    aria-label="Platform"
                >
                    {buttons.map(button => (
                        <ToggleButton
                            key={button}
                            value={button}>
                            {button}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>

                {/* Selected Product List */}
                <div style={{ width: 800 }}>
                    <SelectedProductList
                        selectedProducts={selectedProducts}
                        onDelete={handleDeleteSelectedProduct}
                    />
                </div>
            </div>

            {/* Selectors and Avarage*/}
            {selectedProducts.length === 0 && (
                <div className={styles.selectorContainer}>
                    <Selector
                        label={'Days'}
                        value={days}
                        options={daysOptions}
                        onChange={handleChange(setDays)}
                        className={styles.selector}
                    />

                    <Selector
                        label={'Hours'}
                        value={hours}
                        options={hoursOptions}
                        onChange={handleChange(setHours)}
                        className={styles.selector}
                    />

                    <Selector
                        label={'Minutes'}
                        value={minutes}
                        options={minutesOptions}
                        onChange={handleChange(setMinutes)}
                        className={styles.selector}
                    />

                    {reports.length > 0 && (
                        <div
                            className={styles.avarageContainer}
                            style={{ color: avarageRate > SUCCESS_PERCENTAGE_RATE ? colors.GREEN : colors.WARM_RED }}>
                            <Tx>g.average</Tx>: {avarageRate}%
                        </div>
                    )}
                </div>
            )}

            {/* Table */}
            <div className={styles.tableContainer}>
                {reports.map(({ _id, product, countGroundTruth1, countGroundTruth2 }) => {
                    const countTotal = countGroundTruth1 + countGroundTruth2;
                    const successRate = (countGroundTruth1 / countTotal * 100).toFixed(2);

                    return (
                        <div key={_id} className={styles.productContainer}>
                            <Product
                                key={_id}
                                style={{ flex: 4 }}
                                readonly
                                {...product}
                            />
                            <Card className={styles.percentageTextContainer}>
                                <span
                                    style={{ color: successRate > SUCCESS_PERCENTAGE_RATE ? colors.GREEN : colors.WARM_RED }}>
                                    {successRate}% <span>/ {countTotal}</span>
                                </span>
                            </Card>
                        </div>
                    );
                })}
            </div>

            {/* Selected Product Dialog */}
            {isDialogVisible && (
                <AIReportProductsDialog
                    isVisible={isDialogVisible}
                    onClose={handleCloseDialog}
                    onSave={handleSaveDialog}
                />
            )}
        </div>
    );
}

export default AIReportPage;
